/**
 * ARIA Chat Interface Component
 *
 * Renders the floating chat widget. When the assistant returns structured
 * results (jobs, warranties, technicians) they appear as clickable record
 * cards below the message bubble. Job cards open JobDetailsModal inline;
 * warranty cards show full detail inline and link to the warranty search page.
 */

import { useState, useRef, useEffect } from 'react';
import { useMutation, gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare, Send, X, Minimize2, Maximize2, Bot, User, Loader2,
  ExternalLink, Briefcase, ShieldCheck, HardHat,
} from 'lucide-react';
import { clsx } from 'clsx';
import { JobDetailsModal } from './jobs/JobDetailsModal';
import type { ChatResult, ChatResultDetail } from '../types';

// ─── GraphQL ─────────────────────────────────────────────────────────────────

const SEND_CHAT_MESSAGE = gql`
  mutation SendChatMessage($input: ChatMessageInput!) {
    sendChatMessage(input: $input) {
      message
      actionsTaken { tool args result }
      suggestions
      results {
        type id title subtitle status
        detail { key value }
      }
    }
  }
`;

// ─── Local types ─────────────────────────────────────────────────────────────

interface LocalMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  suggestions?: string[];
  results?: ChatResult[];
}

interface ChatInterfaceProps {
  isOpen?: boolean;
  onClose: () => void;
}

// ─── Status badge helpers ─────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  ACTIVE:           'bg-green-100 text-green-700',
  AVAILABLE:        'bg-green-100 text-green-700',
  COMPLETED:        'bg-green-100 text-green-700',
  SCHEDULED:        'bg-blue-100 text-blue-700',
  IN_PROGRESS:      'bg-blue-100 text-blue-700',
  PENDING_PARTS:    'bg-yellow-100 text-yellow-700',
  READY_TO_SCHEDULE:'bg-yellow-100 text-yellow-700',
  PENDING_APPROVAL: 'bg-yellow-100 text-yellow-700',
  PENDING_DEBRIEF:  'bg-yellow-100 text-yellow-700',
  ON_HOLD:          'bg-yellow-100 text-yellow-700',
  EXPIRED:          'bg-red-100 text-red-700',
  CANCELLED:        'bg-red-100 text-red-700',
  VOID:             'bg-gray-100 text-gray-600',
};

function statusClass(status?: string): string {
  return STATUS_COLORS[status?.toUpperCase() ?? ''] ?? 'bg-gray-100 text-gray-600';
}

const TYPE_ICONS: Record<string, React.ReactNode> = {
  job:         <Briefcase className="w-3.5 h-3.5 text-brand-500" />,
  warranty:    <ShieldCheck className="w-3.5 h-3.5 text-green-600" />,
  technician:  <HardHat className="w-3.5 h-3.5 text-yellow-600" />,
};

// ─── Result card ─────────────────────────────────────────────────────────────

interface ResultCardProps {
  result: ChatResult;
  onOpenJob: (id: string) => void;
  onOpenWarranty: (customerName: string) => void;
}

function ResultCard({ result, onOpenJob, onOpenWarranty }: ResultCardProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-2.5 shadow-sm">
      {/* Header row */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {TYPE_ICONS[result.type]}
          <span className="font-medium text-sm text-gray-900 truncate">{result.title}</span>
        </div>
        {result.status && (
          <span className={clsx('shrink-0 text-xs font-medium px-1.5 py-0.5 rounded-full', statusClass(result.status))}>
            {result.status.replace(/_/g, ' ')}
          </span>
        )}
      </div>

      {/* Subtitle (address / zone) */}
      {result.subtitle && (
        <p className="mt-0.5 text-xs text-gray-500 pl-5 truncate">{result.subtitle}</p>
      )}

      {/* Detail rows */}
      {result.detail.length > 0 && (
        <dl className="mt-1.5 pl-5 grid grid-cols-2 gap-x-3 gap-y-0.5">
          {result.detail.map((d: ChatResultDetail) => (
            <div key={d.key} className="flex gap-1 text-xs">
              <dt className="text-gray-400 shrink-0">{d.key}:</dt>
              <dd className="text-gray-700 truncate">{d.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {/* Action button */}
      <div className="mt-2 pl-5">
        {result.type === 'job' && (
          <button
            onClick={() => onOpenJob(result.id)}
            className="text-xs text-brand-600 hover:text-brand-800 font-medium flex items-center gap-1"
          >
            Open Details <ExternalLink className="w-3 h-3" />
          </button>
        )}
        {result.type === 'warranty' && (
          <button
            onClick={() => onOpenWarranty(result.title)}
            className="text-xs text-brand-600 hover:text-brand-800 font-medium flex items-center gap-1"
          >
            View in Warranties <ExternalLink className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ChatInterface({ isOpen = true, onClose }: ChatInterfaceProps) {
  const navigate = useNavigate();

  const [messages, setMessages] = useState<LocalMessage[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content:
        "Hi! I'm ARIA, your scheduling assistant. I can help you with:\n- Searching for jobs\n- Checking technician availability\n- Looking up warranties\n- Scheduling service appointments\n\nWhat would you like to do?",
      timestamp: new Date(),
      suggestions: ['Search jobs', 'Check availability', 'Warranty lookup'],
    },
  ]);

  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [sendMessage, { loading }] = useMutation(SEND_CHAT_MESSAGE);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (text?: string) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;

    const convId = conversationId ?? `conv-${Date.now()}`;
    if (!conversationId) setConversationId(convId);

    const userMessage: LocalMessage = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');

    try {
      const result = await sendMessage({
        variables: { input: { content, conversationId: convId } },
      });

      const data = result.data?.sendChatMessage;
      if (data) {
        setMessages((prev) => [
          ...prev,
          {
            id: `${Date.now()}-resp`,
            role: 'assistant',
            content: data.message,
            timestamp: new Date(),
            suggestions: data.suggestions,
            results: data.results ?? [],
          },
        ]);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: "I'm sorry, I encountered an error. Please try again.",
          timestamp: new Date(),
        },
      ]);
    }
  };

  // Suggestions send immediately — no fill-then-submit
  const handleSuggestionClick = (suggestion: string) => {
    handleSend(suggestion);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleOpenWarranty = (customerName: string) => {
    navigate(`/warranty?q=${encodeURIComponent(customerName)}`);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Job details modal — triggered by result card clicks */}
      {selectedJobId && (
        <JobDetailsModal
          jobId={selectedJobId}
          isOpen={true}
          onClose={() => setSelectedJobId(null)}
        />
      )}

      <div
        className={clsx(
          'fixed bottom-4 right-4 bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col z-50 transition-all',
          isMinimized ? 'w-72 h-14' : 'w-96 h-[560px]',
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-brand-600 text-white rounded-t-lg">
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5" />
            <span className="font-medium">ARIA Assistant</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1 hover:bg-brand-500 rounded"
            >
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </button>
            <button onClick={onClose} className="p-1 hover:bg-brand-500 rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Message list */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={clsx(
                    'flex gap-3',
                    message.role === 'user' ? 'flex-row-reverse' : 'flex-row',
                  )}
                >
                  {/* Avatar */}
                  <div
                    className={clsx(
                      'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                      message.role === 'user' ? 'bg-brand-100' : 'bg-gray-100',
                    )}
                  >
                    {message.role === 'user' ? (
                      <User className="w-4 h-4 text-brand-600" />
                    ) : (
                      <Bot className="w-4 h-4 text-gray-600" />
                    )}
                  </div>

                  {/* Content column */}
                  <div className={clsx('max-w-[80%] space-y-2', message.role === 'user' && 'items-end flex flex-col')}>
                    {/* Bubble */}
                    <div
                      className={clsx(
                        'rounded-lg px-3 py-2',
                        message.role === 'user'
                          ? 'bg-brand-600 text-white'
                          : 'bg-gray-100 text-gray-900',
                      )}
                    >
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>

                    {/* Result cards — shown below assistant bubbles */}
                    {message.role === 'assistant' && message.results && message.results.length > 0 && (
                      <div className="w-full space-y-1.5">
                        {message.results.map((result) => (
                          <ResultCard
                            key={result.id}
                            result={result}
                            onOpenJob={setSelectedJobId}
                            onOpenWarranty={handleOpenWarranty}
                          />
                        ))}
                      </div>
                    )}

                    {/* Suggestion buttons — shown below assistant bubbles */}
                    {message.role === 'assistant' && message.suggestions && message.suggestions.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {message.suggestions.map((s, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleSuggestionClick(s)}
                            className="text-xs px-2 py-1 bg-brand-50 hover:bg-brand-100 border border-brand-200 rounded-full text-brand-700 transition-colors"
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Typing indicator */}
              {loading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-gray-600" />
                  </div>
                  <div className="bg-gray-100 rounded-lg px-3 py-2">
                    <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input bar */}
            <div className="p-3 border-t border-gray-200">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask me anything..."
                  className="flex-1 input text-sm"
                  disabled={loading}
                />
                <button
                  onClick={() => handleSend()}
                  disabled={!input.trim() || loading}
                  className={clsx(
                    'btn-primary p-2',
                    (!input.trim() || loading) && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ─── Toggle button ────────────────────────────────────────────────────────────

export function ChatToggleButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="fixed bottom-4 right-4 w-14 h-14 bg-brand-600 text-white rounded-full shadow-lg hover:bg-brand-700 flex items-center justify-center z-40 transition-colors"
    >
      <MessageSquare className="w-6 h-6" />
    </button>
  );
}
